import { palette } from '@/src/themes'
import { PATIENT_MODAL_TYPE, useModalStore } from '@modal'
import { ColumnDef } from '@tanstack/react-table'
import { EncounterDataMock, Included } from '@type/encounter.type'
import { Badge, Button, Table2 } from '@ui'
import moment from 'moment'
import { useMemo } from 'react'
import type { PortalVisitSummaryRes } from '../../../../../api/patientSummary'
import { patientHistoryTableClassName } from '../Results/tableStyles'

type Props = {
  included?: Included[]
  isLoading?: boolean
  records: EncounterDataMock[]
  // Completed summaries only (usePortalVisitSummaries) — matched to a row by encounter_id. Empty
  // when the feature is disabled or the list failed to load, so the action just never appears.
  summaries?: PortalVisitSummaryRes[]
}

export default function EncountersTable({ included = [], isLoading, records, summaries = [] }: Props) {
  const { openModal } = useModalStore()

  const columns = useMemo<ColumnDef<EncounterDataMock>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'Encounter ID',
        cell: ({ row }) => <span>{row.original.id}</span>
      },
      {
        accessorKey: 'attributes.created_at',
        header: 'Date',
        cell: ({ row }) => (
          <span>
            {row.original.attributes.created_at
              ? `${moment(row.original.attributes.created_at).format('DD/MM/YYYY')}, ${moment
                  .utc(row.original.attributes.created_at)
                  .format('hh:mm A')}`
              : '-'}
          </span>
        )
      },
      {
        accessorKey: 'provider',
        header: 'Provider',
        cell: ({ row }) => {
          const provider = included.find(
            ({ type, id }) => type === 'staff' && id === row.original.relationships.appointed_staff.data?.id
          )
          return (
            <span className="capitalize">
              {[provider?.attributes.first_name, provider?.attributes.last_name].filter(Boolean).join(' ') || '-'}
            </span>
          )
        }
      },
      {
        accessorKey: 'attributes.encounter_type',
        header: 'Visit type',
        cell: ({ row }) => <span className="capitalize">{row.original.attributes.encounter_type}</span>
      },
      {
        accessorKey: 'meta.status',
        header: 'Status',
        cell: ({ row }) => <Badge label={row.original.meta.status} size="small" status={row.original.meta.status} />
      },
      // Only when the patient has at least one completed visit summary — the list is always empty
      // with the feature off (usePortalVisitSummaries doesn't fetch), so no empty column then.
      ...(summaries.length > 0
        ? [
            {
              id: 'summary',
              header: '',
              // Big, obvious action — patients are not expected to be tech-savvy — shown only for an
              // encounter that has a completed visit summary (matched by encounter_id).
              cell: ({ row }: { row: { original: EncounterDataMock } }) => {
                const match = summaries.find(({ attributes }) => String(attributes.encounter_id) === row.original.id)
                if (!match) return null
                return (
                  <Button
                    kinds="primary"
                    label="Visit summary"
                    onClick={() => openModal(PATIENT_MODAL_TYPE.VIEW_VISIT_SUMMARY, { id: match.id })}
                    size="sm"
                  />
                )
              }
            } as ColumnDef<EncounterDataMock>
          ]
        : []),
      {
        id: 'view',
        header: '',
        cell: ({ row }) => (
          <span
            onClick={() =>
              openModal(PATIENT_MODAL_TYPE.VIEW_ENCOUNTER_DETAILS, {
                defaultData: row.original,
                included,
                id: row.original.id
              })
            }
            style={{ cursor: 'pointer', fontWeight: 500, textDecoration: 'underline', color: palette.primary }}
          >
            View details
          </span>
        )
      }
    ],
    [included, openModal, summaries]
  )

  return (
    <Table2
      className={patientHistoryTableClassName}
      columns={columns}
      data={records}
      emptyDescription="No encounters found."
      isLoading={isLoading}
      message="No encounters found"
      showPagination={false}
      wrapperClass="overflow-x-auto"
    />
  )
}
